import {Component, OnInit, Inject, OnDestroy} from '@angular/core';
import {AutoDestroy, SessionStorageService, WINDOW} from '@ox/utils';
import {MyPlansUnifiedService, ShopDataService} from '@webu/data';
import {ManagePlanPageSettings} from './ManagePlanPageSettings';
import {APP_BASE_HREF} from '@angular/common';
import {Subject, forkJoin, lastValueFrom, map, take, takeUntil, takeWhile} from 'rxjs';
import {OX_KEYS} from '@ox/config';
import {Store} from '@ngrx/store';

import {
  Copy,
  EmitObject,
  ManagePlanPageInfo,
  UIFeatureFlag,
  Plans,
  Prices,
  ProductCopy,
  StepperReturnState,
  StepperValue,
  UpdatePlan,
  UpdatePlanOffers,
  Vehicle
} from '@ox/typings';

import {MyPlansActions, TrackingService, selectFeatureFlag, selectMyCurrentPlans} from '@ox/data';
import {selectActiveVehicle} from '@ox/data';
import {Router} from '@angular/router';
import {CheckoutNavigationService} from '../../services';

@Component({
  selector: 'webu-update-plans',
  templateUrl: './update-plan.component.html'
})
export class UpdatePlansComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  pageType: string = OX_KEYS.MY_PLANS.MANAGE_KEY;
  isError = false;
  isLoading = true;
  copy: Copy;
  updatePlanData: UpdatePlan;
  products: ProductCopy[];
  selectedProduct: Plans;
  pageSettings: ManagePlanPageInfo;
  managePlanPageSettings: ManagePlanPageSettings;
  footerObj: EmitObject;
  tabSelected = 1;
  public activeVehicle$ = this.store.select(selectActiveVehicle);
  myCurrentPlans: string[];
  isCheckoutLoading = false;

  private goToNewCheckout = true;

  newCheckoutFlag$ = this.store
    .select(selectFeatureFlag({id: UIFeatureFlag.CHECKOUT, group: 'UI'}))
    .pipe(
      takeWhile((val) => !val, true),
      map((flag) => this.goToNewCheckout = flag === 'true')
    ).subscribe();

  constructor(
    private sessionStorageService: SessionStorageService,
    private myPlansUnifiedService: MyPlansUnifiedService,
    private shopDataService: ShopDataService,
    @Inject(APP_BASE_HREF) private _baseHref: string,
    public trackingService: TrackingService,
    private store: Store,
    private router: Router,
    @Inject(WINDOW) private window: Window,
    private checkoutNavigationService: CheckoutNavigationService
  ) {}

  async ngOnInit() {
    this.store.dispatch(MyPlansActions.load());
    this.store
      .select(selectMyCurrentPlans)
      .pipe(take(1))
      .subscribe((myCurrentPlans) => {
        this.myCurrentPlans = myCurrentPlans ?? [];
      });
    this.managePlanPageSettings = new ManagePlanPageSettings(this.store);
    this.pageSettings = this.managePlanPageSettings.getPageType(this.pageType);
    this.myPlansUnifiedService?.hideHeaderFooter();
    try {
      const vehicle = await lastValueFrom(this.activeVehicle$.pipe(take(1)));
      if (!vehicle) throw new Error('No active vehicle');
      this.getData(vehicle.vin);
    } catch (error) {
      console.error(error);
    }
  }

  ngOnDestroy(): void {
    this.myPlansUnifiedService?.showHeaderFooter();
  }

  getData(vin: string) {
    const updatePlansApi$ = this.myPlansUnifiedService?.getUpdatePlans(vin);
    const copyApi$ = this.shopDataService?.getCopyData();

    forkJoin([copyApi$, updatePlansApi$]).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: ([copyResponse, updatePlansResponse]) => {
        if (copyResponse.products && updatePlansResponse) {
          this.copy = copyResponse;
          this.updatePlanData = updatePlansResponse;
          this.sessionStorageService.setAsJson('copyData', [{
            'disclosure': this.getCopy('price.disclosure.modal') || ''
          }]);
          this.setDefaultTab();
          this.setPageCopy();
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.onError(error);
      }
    });
  }

  onError(err: any) {
    console.error(err);
    this.isError = true;
    this.isLoading = false;

    let codeindex = 0;
    if (err?.code && err?.subCode) {
      OX_KEYS.B2C_ERROR.BFF.ERROR_KEY_VALUES.forEach((item, index) => {
        if (item.CODE === `${err?.code}-${err?.subCode}`) {
          codeindex = index;
        }
      });
    }
    this.trackingService.trackEvent(
      'event25',
      OX_KEYS.B2C_ERROR.BFF.ERROR_KEY_VALUES[codeindex].CODE
    );
  }

  setPageCopy() {
    this.products = this.updatePlanData?.featuredPlans?.map((item: Plans) => {
      const product = this.copy?.products?.find((product: ProductCopy) => product.code === item.planCode);
      const offer = this.updatePlanData?.offers?.find((offer: UpdatePlanOffers) => offer.planCode === item.planCode);
    
      if (product) {
        product.compositeProducts = offer?.compositeProducts || [];
      }
    
      return product!;
    }) ?? [];
  }

  setDefaultTab() {
    const storedFooterObj: EmitObject = this.sessionStorageService.getFromJson('FooterInfo');

    const isProductWithoutResidue = storedFooterObj?.product && !storedFooterObj.residueBYO.length;
    const isSingleFeaturedPlan = this.myCurrentPlans.length === 1 && 
      this.updatePlanData?.featuredPlans?.find((offer) => offer.planCode === this.myCurrentPlans[0]);
  
    if (isProductWithoutResidue || isSingleFeaturedPlan) {
      this.tabSelected = 0;
    }

    const hasResidueWithoutProduct = storedFooterObj && !storedFooterObj.product && storedFooterObj.residueBYO && storedFooterObj.residueBYO.length;

    if (hasResidueWithoutProduct) {
      this.tabSelected = 1;
    }
  }

  onFooterButtonClick() {
    this.isCheckoutLoading = true;
    const lengthResiduePrepaid = this.footerObj?.residueBYO.filter(
      (item: Plans) => {
        let prepaid = item.offer?.prices.find(
          (item: Prices) =>
            item.billingFrequency === OX_KEYS.MY_PLANS.PREPAID_KEY
        );
        if (!prepaid) {
          prepaid = item.offer?.linkedOffers
            ?.map((item) =>
              this.updatePlanData?.offers?.find(
                (offer: UpdatePlanOffers) => offer.planCode === item
              )
            )
            .find((item) =>
              item?.prices.find(
                (item: Prices) =>
                  item.billingFrequency === OX_KEYS.MY_PLANS.PREPAID_KEY
              )
            )
            ?.prices.find(
              (item: Prices) =>
                item.billingFrequency === OX_KEYS.MY_PLANS.PREPAID_KEY
            );
          if (prepaid) {
            item.offer?.prices.push(prepaid);
          }
        }
        return prepaid;
      }
    ).length;

    this.sessionStorageService.setAsJson('FooterInfo', this.footerObj);

    const isProductPrepaid = !!this.footerObj?.product?.offer?.prices.find(
      (item: Prices) => item.billingFrequency === OX_KEYS.MY_PLANS.PREPAID_KEY
    );

    const offerMonthly = this.footerObj?.product?.offer?.prices.find(
      (item: Prices) => item.billingFrequency === OX_KEYS.MY_PLANS.MONTHLY_KEY
    );
    let offerIds;
    const planCodes: Array<string> = [];
    if(this.goToNewCheckout) {
      this.footerObj.residueBYO.map((item) => planCodes.push(item.code));
      offerIds = this.footerObj.residueBYO.map((item) => item.price.offerId);
    }
    else offerIds = this.footerObj.residueBYO.map((item) => item.price.offerId);
  
    if (offerMonthly?.offerId) {
      if (this.goToNewCheckout && this.footerObj?.product?.offer?.planCode)
        planCodes.push(this.footerObj.product.offer.planCode);
      else
        offerIds.push(offerMonthly.offerId);
    }

    const showError = offerIds? '' : 'true';
  
    if (this.goToNewCheckout) {
      this.checkoutNavigationService
        .goToCheckout(planCodes, this.router.url, showError, undefined, undefined, 'updateplans')
        .pipe(take(1))
        .subscribe({complete: () => this.isCheckoutLoading = false});
    } else {
      this.isCheckoutLoading = false;
      if (this.footerObj.residueBYO.length === lengthResiduePrepaid &&
        ((this.footerObj.product && isProductPrepaid) || !this.footerObj.product)) {
        this.navigateToYearlyBilling();
      } else {
        this.navigateToReview(offerIds);
      }
    }
  }

  navigateToReview(offerIds: (string | undefined)[]) {
    this.sessionStorageService.setAsJson(
      OX_KEYS.MY_PLANS.REVIEW_ID_SESSION_KEY,
      offerIds
    );
    this.sessionStorageService.setAsJson('ManagePlanInfo', {
      pageType: 'MANAGE_REVIEW',
      offerId: offerIds
    });
    this.window.location.href = `${this._baseHref}onstar/review`;
  }

  navigateToYearlyBilling() {
    this.window.location.href = `${this._baseHref}onstar/myplans/yearly-billing`;
  }

  navigateToCheckout(planCodes: (string | undefined)[]) {
    const checkoutData = {
      planCodes: planCodes,
      page: 'updatePlans'
    };
    this.router.navigate(['./shop/checkout', checkoutData]);
  }

  selectedProductChange(planInfo: any) {
    this.selectedProduct = planInfo;
  }

  getSteps(): StepperValue[] {
    const steps: StepperValue[] = [];
    this.pageSettings.flow.forEach((step) => {
      steps.push(
        this.managePlanPageSettings.getPageType(step).stepper.stepInfo
      );
    });

    return steps;
  }

  getCurrentStep(): number {
    return this.pageSettings.flow.indexOf(this.pageType);
  }

  getReturnState(): StepperReturnState {
    return this.pageSettings.stepper.returnState;
  }

  selectedProductAndResidue(event: EmitObject) {
    setTimeout(() => {
      const obj: EmitObject = {
        planCount: event.residueBYO.length,
        residueBYO: event.residueBYO,
        selectedBYO: event.selectedBYO,
        price: parseFloat('' + event.price).toFixed(2),
        selectedProductName: event.product ? event.product.code : '',
        product: event.product,
        selectedProductDisclosure: '',
        offerIds: event.offerIds,
        priceDisclosure: event.priceDisclosure,
        isMonthly: true
      };
      const product =
        obj.selectedProductName &&
        this.products.find((item) => item && item.code === obj.selectedProductName);

      obj.selectedProductDisclosure = (product && product.disclosure) || '';
      obj.offerIds = event.offerIds;
      this.footerObj = obj;
    });
  }

  getCopy(copyKey: string) {
    return this.shopDataService?.getGenericCopy(copyKey);
  }

  fireTracking(button: string) {
    const trackingString = `shop:update plan:button:${button}`;
    this.trackingService.fireProp10Tracking(
      trackingString, '', '', button, '', true
    );
  }
}
